import { AxiosInstance } from "axios";
import { IInvite } from "../interfaces/IInvite";
import { IAiInsights } from './dashboardService';
import type { IPagedFilterResponse } from '../interfaces/IPagedFilterResponse';
import type { ICommunityPost } from '../interfaces/ICommunityHub';
import { getApiErrorMessage } from '../utils/apiErrorUtils';

export type CommunityShareMode = 'organiser' | 'participant';

export interface IEventShareDestination {
    id: string;
    name: string;
    channelId: string;
}

export interface IInviteStats {
    total: number;
    pending: number;
    registered: number;
    withdrawn: number;
    expired: number;
}

export interface IInvitePagedResponse {
    items: IInvite[];
    totalCount: number;
    page: number;
    pageSize: number;
    stats: IInviteStats;
}

export interface IEventCommunityShareResult {
    communityId: string;
    communityName: string;
    postId: string;
    memberCount: number;
    invitesQueuedCount: number;
}

export interface ICommunityInviteStats {
    communityId: string;
    communityName: string;
    communityImageUrl?: string;
    memberCount: number;
    registeredCount: number;
    pendingCount: number;
    sharedAtUtc: string;
}

export class InviteService {
    protected apiClient: AxiosInstance;

    constructor(apiClient: AxiosInstance) {
        this.apiClient = apiClient;
    }

    async getAll(eventId: string): Promise<IInvite[]> {
        try {
            const response = await this.apiClient.get(`/admin/event/${eventId}/invite`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async getAllPaged(
        eventId: string,
        page: number,
        pageSize: number,
        status?: number,
        search?: string
    ): Promise<IInvitePagedResponse> {
        try {
            const params: Record<string, string | number> = { page, pageSize };
            if (status !== undefined) params.status = status;
            if (search) params.search = search;
            const response = await this.apiClient.get(`/admin/event/${eventId}/invite/paged`, { params });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async get(eventId: string, id: string): Promise<IInvite> {
        try {
            const response = await this.apiClient.get(`/admin/event/${eventId}/invite/${id}`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async create(eventId: string, invite: IInvite): Promise<IInvite> {
        try {
            const response = await this.apiClient.post(`/admin/event/${eventId}/invite`, invite);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async resend(eventId: string, id: string): Promise<IInvite> {
        try {
            const response = await this.apiClient.post(`/admin/event/${eventId}/invite/${id}/resend`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async revoke(eventId: string, id: string): Promise<void> {
        try {
            await this.apiClient.patch(`/admin/event/${eventId}/invite/${id}/revoke`);
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async delete(eventId: string, id: string): Promise<void> {
        try {
            await this.apiClient.delete(`/admin/event/${eventId}/invite/${id}`);
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async getInviteInsights(eventId: string): Promise<IAiInsights> {
        try {
            const response = await this.apiClient.get(`/admin/event/${eventId}/invite/insights`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async generateMessage(eventId: string, recipientFirstName: string, recipientLastName: string): Promise<string> {
        try {
            const response = await this.apiClient.post(`/admin/event/${eventId}/invite/generate-message`, {
                recipientFirstName,
                recipientLastName,
            });
            // Response is a JSON string e.g. { "message": "..." }
            const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
            return data.message ?? '';
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async suggestDelegates(eventId: string, limit = 10): Promise<{ firstName: string; lastName: string; email: string; reason: string }[]> {
        try {
            const response = await this.apiClient.get(`/admin/event/${eventId}/invite/suggest`, {
                params: { limit }
            });
            return response.data?.suggestions ?? [];
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async getShareDestinations(eventId: string, mode: CommunityShareMode, page: number, search: string, signal?: AbortSignal): Promise<IPagedFilterResponse<IEventShareDestination>> {
        const response = await this.apiClient.get(`/${mode === 'organiser' ? 'admin' : 'participant'}/community-hub/events/${eventId}/share-destinations`, {
            params: { page, search: search.trim() || undefined }, signal,
        });
        return response.data;
    }

    async shareToCommunities(
        eventId: string,
        communityIds: string[],
        message: string | undefined,
        notifyMembers: boolean,
        mode: CommunityShareMode = 'organiser'
    ): Promise<IEventCommunityShareResult[]> {
        try {
            if (mode === 'participant') {
                const response = await this.apiClient.post<ICommunityPost[]>(`/participant/community-hub/events/${eventId}/share`, { communityIds, message });
                return response.data.map(post => ({
                    communityId: post.communityId, communityName: post.communityName ?? '',
                    postId: post.id, memberCount: 0, invitesQueuedCount: 0,
                }));
            }
            const response = await this.apiClient.post(`/admin/event/${eventId}/invite/communities`, {
                communityIds,
                message,
                notifyMembers,
            });
            return response.data;
        } catch (error: unknown) {
            throw new Error(getApiErrorMessage(error, 'Your event could not be shared. Try again.'));
        }
    }

    async getCommunityStats(eventId: string): Promise<ICommunityInviteStats[]> {
        try {
            const response = await this.apiClient.get(`/admin/event/${eventId}/invite/community-stats`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }

    async resendCommunityShare(eventId: string, communityId: string): Promise<number> {
        try {
            const response = await this.apiClient.post(`/admin/event/${eventId}/invite/communities/${communityId}/resend`);
            return response.data?.resentCount ?? 0;
        } catch (error: any) {
            throw new Error(error.response ? error.response.data : 'An error occurred');
        }
    }
}